import type { AuthUser } from "../components/auth/AuthProvider";
import type { AdminPermission } from "./acl/permissions";

const normalize = (value: string | null | undefined) => value?.trim().toLowerCase() ?? "";

/**
 * Check if a user account is banned/suspended
 */
export const isBannedUser = (user: AuthUser | null | undefined): boolean => {
  if (!user) return false;
  return user.isBanned === true;
};

/**
 * Check if a user has admin-level access
 * This is a convenience helper; prefer checking specific permissions when available.
 */
export const isAdminUser = (user: AuthUser | null | undefined): boolean => {
  if (!user) return false;

  // userLevel >= 9 indicates admin access (legacy check)
  if (typeof user.userLevel === "number" && user.userLevel >= 9) return true;

  // Check role string
  const role = normalize(user.role);
  if (role === "admin" || role === "super admin" || role === "superadmin" || role === "administrator") {
    return true;
  }

  // Demo admin fallback
  return normalize(user.username) === "demo_admin";
};

/**
 * Check if a user is specifically a Super Admin
 */
export const isSuperAdminUser = (user: AuthUser | null | undefined): boolean => {
  if (!user) return false;

  // userLevel >= 10 indicates super admin
  if (typeof user.userLevel === "number" && user.userLevel >= 10) return true;

  const role = normalize(user.role);
  return role === "super admin" || role === "superadmin";
};

/**
 * Check if a user has staff-level access (includes admin)
 */
export const isStaffUser = (user: AuthUser | null | undefined): boolean => {
  if (!user) return false;

  // Admin includes staff
  if (isAdminUser(user)) return true;

  // userLevel >= 5 indicates staff
  if (typeof user.userLevel === "number" && user.userLevel >= 5) return true;

  const role = normalize(user.role);
  return role === "staff" || role === "moderator";
};

/**
 * Check if a user has a specific admin permission.
 * Super admins automatically have all permissions.
 */
export const hasPermission = (user: AuthUser | null | undefined, permission: AdminPermission): boolean => {
  if (!user) return false;

  // Super admins have all permissions
  if (isSuperAdminUser(user)) return true;

  // Check explicit permissions
  if (user.permissions?.includes(permission)) return true;

  // Legacy fallback: Admins (userLevel >= 9) have all permissions
  // This ensures backward compatibility until roles are fully migrated
  if (isAdminUser(user)) return true;

  return false;
};

/**
 * Check if a user has any of the given permissions.
 */
export const hasAnyPermission = (user: AuthUser | null | undefined, permissions: AdminPermission[]): boolean => {
  return permissions.some((p) => hasPermission(user, p));
};

/**
 * Check if a user has all of the given permissions.
 */
export const hasAllPermissions = (user: AuthUser | null | undefined, permissions: AdminPermission[]): boolean => {
  return permissions.every((p) => hasPermission(user, p));
};
